import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Loader2, X, Heart, Trash2, Plus, Minus } from "lucide-react";
import { ProductContext } from "../../@types/productInfo";

interface CartTableProps {
  cart: any[];
  onIncrement: (product: ProductContext) => void;
  onDecrement: (product: ProductContext) => void;
  onRemove: (product: ProductContext) => void;
  itemStocks: Record<string, number>;
  isUpdating: boolean;
  isDecreasing: boolean;
  onSaveForLater?: (id: string) => void;
  isRemoving: boolean;
  isBlack?: boolean;
  isMachine?: boolean;
  fetchStockForAllItems: () => void;
}

interface CartItem extends ProductContext {
  color?: string;
  images_black?: string[];
  images_white?: string[];
  black_image?: string;
  white_image?: string;
  availableStock?: number;
  quantity: number;
  item_quantity: number;
}

const CartItemMobile: React.FC<CartTableProps> = ({
  cart,
  onIncrement,
  onDecrement,
  onRemove,
  itemStocks,
  isUpdating,
  isDecreasing,
  onSaveForLater,
  isRemoving,
  isBlack,
  isMachine,
  fetchStockForAllItems,
}) => {


  useEffect(() => {
  
    fetchStockForAllItems();
    
   
    const handleBeforeUnload = () => {
     
      console.log("Page is being refreshed");
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
   
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);


  const getProductImage = (item: CartItem) => {
    return item.image;
  };

  const capitalizeFirstLetter = (str: string | null | undefined) => {
    if (!str) return "";
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const renderStockMessage = (sku: string) => {
    const stockLevel = itemStocks[sku];
  
    if (stockLevel === undefined) {
      return <span className="text-gray-400">Stock information unavailable</span>;
    }
  
    return (
      <div
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
          stockLevel > 0
            ? "bg-green-100 text-green-800"
            : ""
        }`}>
        {stockLevel > 0 ? (
          <>
            <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>{" "}
            In Stock
          </>
        ) : (
          <>
            <span className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>{" "}
           
          </>
        )}
      </div>
    );
  };

  const isOutOfStock = (item: any) => {
    return itemStocks[item?.sku || ""] === 0;
  };

  // Added null-safe price formatting
  const formatPrice = (price: number | null | undefined) => {
    if (price == null) return 'N/A';
    return price.toLocaleString("en-ZA", {
      style: "currency",
      currency: "ZAR",
    });
  };

  const calculateTotalPrice = () => {
    return cart.reduce((total, item) => {
      // Added null checks for price and quantity
      const price = item.price ?? 0;
      const quantity = item.item_quantity ?? 0;
      return total + price * quantity;
    }, 0);
  };

  if (cart.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-gray-500 bg-gray-50 rounded-3xl">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="mb-6 p-6 bg-white rounded-full shadow-sm"
        >
          <ShoppingCart size={64} className="opacity-40 text-indigo-600" />
        </motion.div>
        <h3 className="text-2xl font-bold mb-3 text-gray-800">Your cart is empty</h3>
        <p className="text-gray-500 text-center mb-8 max-w-xs">Looks like you haven't added anything to your cart yet</p>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="px-8 py-4 bg-indigo-600 text-white rounded-full font-medium shadow-lg shadow-indigo-200 flex items-center gap-2"
        >
          <ShoppingCart size={20} />
          Continue Shopping
        </motion.button>
      </div>
    );
  }

  return (
    <div className="pb-4">
      <div className="space-y-5 mb-6">
        <AnimatePresence>
          {cart.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100"
            >
              <div className="p-5">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-bold text-gray-900 text-lg pr-4 line-clamp-1">
                    {capitalizeFirstLetter(item.name)}
                  </h3>
                  <div className="flex space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onSaveForLater && onSaveForLater(item.id)}
                      className="text-gray-400 hover:text-pink-500 p-2 rounded-full hover:bg-pink-50 transition-colors"
                    >
                      {/* <Heart size={18} strokeWidth={2} /> */}
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onRemove(item)}
                      className="text-gray-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors"
                    >
                      {isRemoving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 size={18} strokeWidth={2} />
                      )}
                    </motion.button>
                  </div>
                </div>

                <div className="flex gap-5">
                  <div className="relative w-28 h-28 flex-shrink-0 bg-gray-50 rounded-xl overflow-hidden">
                    <motion.img
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                      className="w-full h-full object-contain p-2"
                      src={getProductImage(item)}
                      alt={capitalizeFirstLetter(item.name)}
                    />
                    {item.color && (
                      <div 
                        className="absolute bottom-2 right-2 w-5 h-5 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: item.color }}
                      ></div>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xl font-bold text-gray-900">
                          {formatPrice(item.price)}
                        </div>
                        <div className="text-sm font-medium text-gray-500">
                          × {item.item_quantity ?? 0}
                        </div>
                      </div>
                      
                      {item.sku && renderStockMessage(item.sku)}
                    </div>

                    <div className="flex items-center mt-4">
                      <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => onDecrement(item)}
                          disabled={item.item_quantity <= 1}
                          className={`p-2 rounded-full ${
                           item.item_quantity <= 1
                              ? "text-gray-300 cursor-not-allowed"
                              : "text-gray-700 hover:bg-gray-200 active:bg-gray-300"
                          }`}
                        >
                          {isDecreasing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Minus size={16} strokeWidth={2.5} />
                          )}
                        </motion.button>

                        <span className="w-8 text-center font-bold text-gray-900">
                          {item.item_quantity ?? 0}
                        </span>

                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => onIncrement(item)}
                          disabled={isOutOfStock(item)}
                          className={`p-2 rounded-full ${
                            isOutOfStock(item)
                              ? "text-gray-300 cursor-not-allowed"
                              : "text-gray-700 hover:bg-gray-200 active:bg-gray-300"
                          }`}
                        >
                          {isUpdating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Plus size={16} strokeWidth={2.5} />
                          )}
                        </motion.button>
                      </div>
                      
                      <div className="ml-auto text-right">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Subtotal</div>
                        <div className="font-bold text-gray-900">
                          {formatPrice(
                            (item.price ?? 0) * (item.item_quantity ?? 0)
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CartItemMobile;